import type { AdminFlowHistoryProps } from "../../models/admin-flows.models";
import { ADMIN_FLOW_BUTTON, ADMIN_FLOW_INPUT, ADMIN_FLOW_PANEL } from "../../utils/admin-flows.constants";
import { useState } from "react";

// History reads actual backend run summaries and never fills empty states with fictional activity.
const AdminFlowHistory = ({ runs, onReview, onRefresh }: AdminFlowHistoryProps) => {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [date, setDate] = useState("");
  const matching = runs.filter(
    (run) =>
      (status === "all" || run.status === status) &&
      run.flowName.toLowerCase().includes(search.toLowerCase()) &&
      (!date || run.startedAt.slice(0, 10) === date),
  );

  return (
    <section data-test="admin-flow-history" className={`${ADMIN_FLOW_PANEL} overflow-hidden`}>
      <header className="border-greyscale-20 flex flex-wrap items-center gap-3 border-b p-4">
        <h2 className="mr-auto font-semibold">Run history</h2>
        <input
          aria-label="Search flows"
          data-test="admin-flow-history-search"
          placeholder="Search flow names"
          className={`${ADMIN_FLOW_INPUT} !w-48`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select
          aria-label="Run status"
          data-test="admin-flow-history-status"
          className={`${ADMIN_FLOW_INPUT} !w-44`}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="all">All statuses</option>
          {["running", "completed", "failed", "stopped", "awaiting_permission"].map((value) => (
            <option key={value} value={value}>
              {value.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        <input
          type="date"
          aria-label="Started date (UTC)"
          data-test="admin-flow-history-date"
          className={`${ADMIN_FLOW_INPUT} !w-44`}
          value={date}
          onChange={(event) => setDate(event.target.value)}
        />
        <button data-test="admin-flow-history-refresh" className={ADMIN_FLOW_BUTTON} onClick={onRefresh}>
          Refresh
        </button>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs opacity-60">
            <tr>
              <th className="p-4">Flow</th>
              <th className="p-4">Status</th>
              <th className="p-4">Started</th>
              <th className="p-4">Duration</th>
              <th className="p-4">Trigger</th>
              <th className="p-4">Review</th>
            </tr>
          </thead>
          <tbody>
            {matching.map((run) => (
              <tr key={run.id} className="border-greyscale-20 border-t hover:bg-greyscale-10 dark:hover:bg-gray-700">
                <td className="p-4 font-medium">
                  {run.flowName}
                  <span className="mt-1 block text-xs font-normal opacity-60">Revision {run.flowRevision}</span>
                </td>
                <td className="p-4">
                  <span
                    className={`rounded px-2 py-1 text-xs ${run.status === "completed" ? "bg-[#E0F2EE] text-[#146b5e]" : run.status === "failed" ? "bg-red-50 text-red-700" : "bg-greyscale-10 dark:bg-gray-700"}`}
                  >
                    {run.status.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="whitespace-nowrap p-4">{new Date(run.startedAt).toLocaleString()}</td>
                <td className="p-4">
                  {run.finishedAt
                    ? `${Math.max(0, Math.round((Date.parse(run.finishedAt) - Date.parse(run.startedAt)) / 1000))}s`
                    : "In progress"}
                </td>
                <td className="p-4">Manual</td>
                <td className="p-4">
                  <button
                    data-test={`admin-flow-history-review-${run.id}`}
                    className={ADMIN_FLOW_BUTTON}
                    onClick={() => onReview(run.id)}
                  >
                    Review run
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!matching.length && (
        <div data-test="admin-flow-history-empty" className="p-12 text-center">
          <h3 className="font-semibold">{runs.length ? "No matching runs" : "No runs yet"}</h3>
          <p className="mt-2 text-sm opacity-60">
            {runs.length
              ? "Change the filters to see more activity."
              : "Create a flow and select Test flow. Actual results will appear here."}
          </p>
        </div>
      )}
      <p className="border-greyscale-20 border-t p-4 text-xs opacity-60">
        Temporary backend history · resets on restart · separate instances have separate histories · dates filter in
        UTC.
      </p>
    </section>
  );
};

export default AdminFlowHistory;
